import React from 'react';
import { useTranslation } from 'react-i18next';

import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Menu from '@mui/material/Menu';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import InputAdornment from '@mui/material/InputAdornment';
import TableContainer from '@mui/material/TableContainer';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';

import Iconify from '../../../theme/iconify/iconify';

import { fDate } from '../../util/format-time';
import { mislecAPI } from '../../../util/axios';

function ParticipantList({ users, changeStatus, setRevokeAccessId, setRevokeAccessModal, setOpen, setMessage, setSeverity }) {
    const [sortedUsers, setSortedUsers] = React.useState(users);
    const [filteredUsers, setFilteredUsers] = React.useState(users);
    const [groups, setGroups] = React.useState(null);
    const [search, setSearch] = React.useState('');
    const [order, setOrder] = React.useState('asc');
    const [orderBy, setOrderBy] = React.useState('enrolled');
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(parseInt(localStorage.getItem('mISLec_table_rows_per_page')) || 5);
    const [startIndex, setStartIndex] = React.useState(null);
    const [endIndex, setEndIndex] = React.useState(null);
    const [menuAnchor, setMenuAnchor] = React.useState(null);
    const [userId, setUserId] = React.useState(null);
    const menu = Boolean(menuAnchor);

    const { i18n, t } = useTranslation();
    const CHIP_SIZE = 28;

    const fetchGroups = () => {
        mislecAPI.get(`/api/groups`)
            .then(res => {
                setGroups(res.data.groups);
            })
            .catch(error => {
                //  Notify user of error
                setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                setSeverity('error');
                setOpen(true);
            });
    }

    const addToGroup = (group, user) => {
        //  Data preparation
        const data = {
            id: user
        };

        //  Send data to the server
        mislecAPI.post(`api/groups/${group}/users`, data)
            .then(res => {
                //  Notify user of success
                setMessage(t(res.data.detail));
                setSeverity('success');
                setOpen(true);

                //  Fetch members
                fetchGroups();
            })
            .catch(error => {
                //  Notify user of error
                setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                setSeverity('error');
                setOpen(true);
            });
    }

    const removeFromGroup = (group, user) => {
        //  Send data to the server
        mislecAPI.delete(`api/groups/${group}/users/${user}`)
            .then(res => {
                //  Notify user of success
                setMessage(t(res.data.detail));
                setSeverity('success');
                setOpen(true);

                //  Fetch members
                fetchGroups();
            })
            .catch(error => {
                //  Notify user of error
                setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                setSeverity('error');
                setOpen(true);
            });
    }

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value));
        localStorage.setItem('mISLec_table_rows_per_page', parseInt(event.target.value));
        if (parseInt(event.target.value) === -1) {
            setStartIndex(0);
            setEndIndex(sortedUsers.length);
        } else {
            setStartIndex(page * parseInt(event.target.value));
            setEndIndex(page * parseInt(event.target.value) + parseInt(event.target.value));
        }
        setPage(0);
    };

    const handleRequestSort = (event, property) => {
        if (orderBy === property) {
            if (order === 'asc') {
                setOrder('desc');
            } else {
                setSortedUsers(users);
                setOrderBy(null);
                setOrder('asc');
            }
        } else {
            setOrderBy(property);
            setOrder('asc');
        }
    }

    React.useEffect(() => {
        if (!users) return;

        let sorted = [...users];
        if (orderBy) {
            sorted.sort((a, b) => {
                if (orderBy === 'enrolled_at') {
                    const valueA = a[orderBy] ? new Date(a[orderBy]) : order === 'asc' ? Infinity : -Infinity;
                    const valueB = b[orderBy] ? new Date(b[orderBy]) : order === 'asc' ? Infinity : -Infinity;
                    return (valueA - valueB) * (order === 'desc' ? -1 : 1);
                } else {
                    return (a[orderBy] < b[orderBy] ? -1 : 1) * (order === 'desc' ? -1 : 1);
                }
            });
        }

        setSortedUsers(sorted);
    }, [users, orderBy, order]);

    React.useEffect(() => {
        setFilteredUsers(
            sortedUsers
                .filter(u =>
                    u.email.toLowerCase().includes(search.toLowerCase()) ||
                    u.fname.toLowerCase().includes(search.toLowerCase()) ||
                    u.lname.toLowerCase().includes(search.toLowerCase()) ||
                    `${u.fname.toLowerCase()} ${u.lname.toLowerCase()}`.includes(search.toLowerCase()) ||
                    t(`users.role.${u.role}`).toLowerCase().includes(search.toLowerCase())
                )
        );
    }, [search, sortedUsers]);

    React.useEffect(() => {
        if (!filteredUsers) return;

        if (rowsPerPage === -1) {
            setStartIndex(0);
            setEndIndex(filteredUsers.length);
        } else {
            setStartIndex(page * rowsPerPage);
            setEndIndex(page * rowsPerPage + rowsPerPage);
        }
    }, [filteredUsers, page, rowsPerPage]);

    React.useEffect(() => {
        setPage(0);
    }, [search]);

    React.useEffect(() => {
        if (groups) return;
        fetchGroups();
    }, [groups]);

    return (
        <Grid container sx={{ p: 1, pb: 0, backgroundColor: 'background.default', borderRadius: '6px', border: '1px solid rgba(241, 243, 244, 1)' }}>
            <Grid item xs={12} md={4}>
                <TextField
                    id='mISLec_user_management_search'
                    placeholder={t('course.tabs.participants.inputs.search.label')}
                    variant='outlined'
                    size='small'
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    fullWidth
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position='end'>
                                <IconButton disabled edge='start'>
                                    <Iconify icon='ion:search' />
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                    sx={{
                        '& .MuiInputBase-input': { fontSize: '0.875rem' },
                        mb: 2
                    }}
                />
            </Grid>
            <Grid item xs={12}>
                <TableContainer>
                    <Table sx={{ tableLayout: { xl: 'fixed', xs: 'auto' } }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ backgroundColor: '#FFFFFF' }}>
                                    <TableSortLabel
                                        active={orderBy === 'fname'}
                                        direction={orderBy === 'fname' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'fname')}
                                    >
                                        <Typography sx={{ fontSize: '0.875rem', fontWeight: '700 !important' }}>
                                            {t('users.table.name')}
                                        </Typography>
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ backgroundColor: '#FFFFFF', width: { xs: 'inherit', md: '30%' } }}>
                                    <Typography sx={{ fontSize: '0.875rem', fontWeight: '700 !important' }}>
                                        {t('course.tabs.participants.table.groups')}
                                    </Typography>
                                </TableCell>
                                <TableCell sx={{ backgroundColor: '#FFFFFF' }}>
                                    <TableSortLabel
                                        active={orderBy === 'enrolled_at'}
                                        direction={orderBy === 'enrolled_at' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'enrolled_at')}
                                    >
                                        <Typography sx={{ fontSize: '0.875rem', fontWeight: '700 !important' }}>
                                            {t('course.tabs.participants.table.date')}
                                        </Typography>
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ backgroundColor: '#FFFFFF' }}>
                                    <TableSortLabel
                                        active={orderBy === 'enrolled'}
                                        direction={orderBy === 'enrolled' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'enrolled')}
                                    >
                                        <Typography sx={{ fontSize: '0.875rem', fontWeight: '700 !important' }}>
                                            {t('course.tabs.participants.table.status.title')}
                                        </Typography>
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ backgroundColor: '#FFFFFF', width: { xs: 'inherit', md: '5%' } }} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredUsers
                                .slice(startIndex, endIndex)
                                .map((u, index) => (
                                    <TableRow key={index} sx={{
                                        '&:nth-of-type(even)': {
                                            backgroundColor: '#FFFFFF !important'
                                        },
                                        '&:nth-of-type(odd)': {
                                            backgroundColor: 'background.default'
                                        }
                                    }}>
                                        <TableCell>
                                            <Typography sx={{ fontSize: '0.875rem' }}>
                                                {`${u.fname} ${u.lname}`}
                                            </Typography>
                                            <Typography color='text.secondary' sx={{ fontSize: '0.7rem' }}>
                                                <a href={`mailto:${u.email}`}>
                                                    <span style={{ display: 'inline-block', maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'middle' }}>
                                                        {u.email}
                                                    </span>
                                                </a>
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={groups?.filter(g => !g.members.includes(u._id)).length === 0 ? '' : t('course.tabs.participants.tooltips.addToGroup')} arrow>
                                                <span>
                                                    <Chip
                                                        disabled={groups?.filter(g => !g.members.includes(u._id)).length === 0}
                                                        icon={
                                                            <Iconify icon='tdesign:plus' width={20} sx={{
                                                                color: 'rgba(33, 43, 54, 0.26) !important',
                                                                '&:hover': { color: 'rgba(33, 43, 54, 0.4) !important' }
                                                            }}
                                                            />
                                                        }
                                                        onClick={(event) => {
                                                            if (groups?.filter(g => !g.members.includes(u._id)).length === 0) return;
                                                            event.preventDefault();
                                                            setUserId(u._id);
                                                            setMenuAnchor(event.currentTarget);
                                                        }}
                                                        sx={{
                                                            m: 0.5,
                                                            width: CHIP_SIZE,
                                                            height: CHIP_SIZE,
                                                            borderRadius: '8px !important',
                                                            '& .MuiChip-icon': {
                                                                margin: 0,
                                                            },
                                                            '& .MuiChip-label': {
                                                                display: 'none',
                                                            }
                                                        }}
                                                    />
                                                </span>
                                            </Tooltip>
                                            {groups?.filter(g => g.members.includes(u._id)).map((g) => (
                                                <Chip
                                                    key={g._id}
                                                    label={g.name}
                                                    onDelete={() => removeFromGroup(g._id, u._id)}
                                                    deleteIcon={
                                                        <Tooltip title={t('course.tabs.participants.tooltips.removeFromGroup')} arrow>
                                                            <Iconify icon='eva:close-outline' width={16} />
                                                        </Tooltip>
                                                    }
                                                    sx={{
                                                        m: 0.5,
                                                        height: CHIP_SIZE,
                                                        borderRadius: '8px !important',
                                                        '& .MuiChip-label': {
                                                            px: 1,
                                                        }
                                                    }}
                                                />
                                            ))}
                                            <Menu
                                                key={u._id}
                                                anchorEl={menuAnchor}
                                                anchorOrigin={{
                                                    vertical: 'bottom',
                                                    horizontal: 'center',
                                                }}
                                                transformOrigin={{
                                                    vertical: 'top',
                                                    horizontal: 'center',
                                                }}
                                                open={menu && userId === u._id}
                                                onClose={(event) => {
                                                    event.preventDefault();
                                                    event.stopPropagation();
                                                    setMenuAnchor(null);
                                                    setUserId(null);
                                                }}
                                            >
                                                {groups?.filter(g => !g.members.includes(u._id)).map((g) => (
                                                    <MenuItem
                                                        key={g._id}
                                                        onClick={(event) => {
                                                            event.preventDefault();
                                                            addToGroup(g._id, u._id);
                                                            setMenuAnchor(null);
                                                            setUserId(null);
                                                        }}
                                                    >
                                                        <ListItemIcon sx={{ color: 'primary.main', minWidth: '28px !important' }}>
                                                            <Iconify icon='prime:users' />
                                                        </ListItemIcon>
                                                        <ListItemText>
                                                            {g.name}
                                                        </ListItemText>
                                                    </MenuItem>
                                                ))}
                                            </Menu>
                                        </TableCell>
                                        <TableCell>
                                            <Typography sx={{ fontSize: '0.875rem' }}>
                                                {u.enrolled_at ? fDate(u.enrolled_at, i18n.language) : t('users.table.noRegistration')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography sx={{ fontSize: '0.875rem', color: !u.enrolled ? 'warning.main' : 'text.primary', fontWeight: !u.enrolled ? '700 !important' : '400' }}>
                                                {u.enrolled ? t('course.tabs.participants.table.status.enrolled') : t('course.tabs.participants.table.status.pending')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Stack
                                                direction='row'
                                                spacing={1}
                                                alignItems='center'
                                                justifyContent='flex-end'
                                            >
                                                {!u.enrolled &&
                                                    <>
                                                        <Tooltip title={t('course.tabs.participants.tooltips.approve')} arrow>
                                                            <IconButton
                                                                disableRipple
                                                                disableFocusRipple
                                                                onClick={(event) => {
                                                                    changeStatus(u, true);
                                                                }}
                                                                sx={{ marginRight: 'auto', zIndex: 9, pr: 0, py: 0 }}>
                                                                <Iconify icon='eva:checkmark-outline' />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title={t('course.tabs.participants.tooltips.deny')} arrow>
                                                            <IconButton
                                                                disableRipple
                                                                disableFocusRipple
                                                                onClick={(event) => {
                                                                    changeStatus(u, false);
                                                                }}
                                                                sx={{ marginRight: 'auto', zIndex: 9, pr: 0, py: 0, color: 'error.main' }}>
                                                                <Iconify icon='eva:close-outline' />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </>
                                                }
                                                {u.enrolled &&
                                                    <Tooltip title={t('course.tabs.participants.tooltips.revoke')} arrow>
                                                        <IconButton
                                                            disableRipple
                                                            disableFocusRipple
                                                            onClick={(event) => {
                                                                setRevokeAccessId(u._id);
                                                                setRevokeAccessModal(true);
                                                            }}
                                                            sx={{ marginRight: 'auto', zIndex: 9, pr: 0, py: 0, color: 'error.main' }}>
                                                            <Iconify icon='fluent:arrow-exit-12-regular' width={19} />
                                                        </IconButton>
                                                    </Tooltip>
                                                }
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))
                            }
                            {filteredUsers.length === 0 &&
                                <TableRow>
                                    <TableCell colSpan={6}>
                                        <Grid container>
                                            <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }}>
                                                <Iconify icon='pajamas:search-results' width={36} sx={{ mr: 2 }} />
                                                <Grid item xs={3}>
                                                    <Typography variant='h6' sx={{ fontSize: '0.875rem !important' }}>
                                                        {t('users.table.empty.title')}
                                                    </Typography>
                                                    <Typography variant='caption' sx={{ fontSize: '0.8rem !important' }}>
                                                        {t('users.table.empty.description')}
                                                    </Typography>
                                                    <Typography variant='caption' sx={{ fontSize: '0.8rem !important', display: 'block' }}>
                                                        {t('users.table.empty.tip')}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </TableCell>
                                </TableRow>
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
                {filteredUsers.length > 0 && (
                    <TablePagination
                        component='div'
                        rowsPerPageOptions={[5, 10, 25, 50, { value: -1, label: t('users.table.all') }]}
                        count={filteredUsers.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage={t('users.table.rows')}
                        labelDisplayedRows={({ from, to, count }) => { return `${from}–${to} ${t('util.pagination.of')} ${count !== -1 ? count : `${t('users.table.moreThan')} ${to}`}`; }}
                    />
                )}
            </Grid>
        </Grid>
    );
}

export default ParticipantList;